import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MenuOverlay from '../components/MenuOverlay';
import ShopHero from '../components/shop/ShopHero';
import ProductGrid from '../components/shop/ProductGrid';
import FaqAccordion from '../components/shop/FaqAccordion';
import BeforeAfterSection from '../components/shop/BeforeAfterSection';
import SupportedCameras from '../components/shop/SupportedCameras';
import CartDrawer from '../components/shop/CartDrawer';
import useCart from '../hooks/useCart';
import { PRODUCTS } from '../data/products';

const Shop = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [navSolid, setNavSolid] = useState(false);
  const navigate = useNavigate();
  const cart = useCart();

  const allProducts = PRODUCTS;

  // Mobile detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Nav background on scroll
  useEffect(() => {
    const onScroll = () => setNavSolid(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Enable scrolling
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    html.style.overflow = 'auto';
    html.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.height = 'auto';
    body.style.position = 'static';
    if (root) {
      root.style.overflow = 'auto';
      root.style.height = 'auto';
      root.style.position = 'static';
    }
    return () => {
      html.style.overflow = 'hidden';
      html.style.height = '100%';
      body.style.overflow = 'hidden';
      body.style.height = '100%';
      body.style.position = 'fixed';
      if (root) {
        root.style.overflow = 'hidden';
        root.style.height = '100%';
        root.style.position = 'fixed';
      }
    };
  }, []);

  return (
    <div style={{ background: '#000', minHeight: '100vh' }}>
      {/* Top bar */}
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          height: isMobile ? 56 : 72,
          background: navSolid ? '#000000' : 'transparent',
          transition: 'background 0.4s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '0 12px' : '0 48px',
        }}
      >
        <div style={{ width: 80 }} />
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <h1
            onClick={() => navigate('/')}
            className="tracking-widest uppercase cursor-pointer"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 300,
              fontSize: isMobile ? '14px' : '22px',
              color: '#ffffff',
              margin: 0,
            }}
          >
            Joshua Gavin
          </h1>
        </div>
        <div style={{ width: 80, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 20 }}>
          {/* Cart icon */}
          <button
            onClick={() => setIsCartOpen(true)}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffffff',
              cursor: 'pointer',
              padding: 0,
              position: 'relative',
            }}
            aria-label="Open cart"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>
            {cart.cartCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -8,
                  background: '#36454F',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 600,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Montserrat, sans-serif',
                }}
              >
                {cart.cartCount}
              </span>
            )}
          </button>
          {/* Hamburger */}
          <button
            onClick={() => setIsMenuOpen(true)}
            className="flex flex-col items-end gap-2 bg-transparent border-none cursor-pointer p-0 group"
            aria-label="Open menu"
          >
            <span
              className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12"
              style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
            />
            <span
              className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12"
              style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
            />
            {isMobile && (
              <span
                className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12"
                style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
              />
            )}
          </button>
        </div>
      </nav>

      {/* Sticky hero — image stays fixed, nav overlays it transparently */}
      <div style={{ height: '100vh', position: 'relative' }}>
        <div
          style={{
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflow: 'hidden',
            zIndex: 0,
          }}
        >
          <ShopHero />
        </div>
      </div>

      {/* Content — scrolls over the hero */}
      <div style={{ position: 'relative', zIndex: 1, background: '#000' }}>
        {/* Products */}
        <ProductGrid products={allProducts} />

        {/* Before / After slider */}
        <BeforeAfterSection />

        {/* Supported Cameras */}
        <SupportedCameras />

      {/* Divider */}
      <div style={{ maxWidth: 120, margin: '0 auto', borderTop: '1px solid rgba(255,255,255,0.1)' }} />

      {/* FAQ */}
      <FaqAccordion />

      {/* Footer */}
      <footer
        style={{
          textAlign: 'center',
          padding: '48px 24px 64px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <a
          href="mailto:contact@joshuagavin.com"
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 300,
            fontSize: 14,
            color: 'rgba(255,255,255,0.5)',
            textDecoration: 'none',
            letterSpacing: '0.08em',
          }}
        >
          contact@joshuagavin.com
        </a>
        <p
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 300,
            fontSize: 12,
            color: 'rgba(255,255,255,0.25)',
            marginTop: 16,
            letterSpacing: '0.05em',
          }}
        >
          &copy; {new Date().getFullYear()} Joshua Gavin. All rights reserved.
        </p>
      </footer>
      </div>

      {/* Overlays */}
      <MenuOverlay isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} cart={cart} />
    </div>
  );
};

export default Shop;
